import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TRANSLOCO_SCOPE, TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { AuthService } from '../../../../services/auth.service';
import { UserService } from '../../../../services/user.service';
import { FeedbackService } from '../../../../services/feedback.service';
import { LegalLinksContainerComponent } from '../../../../shared/components/legal-links-container/legal-links-container.component';

@Component({
  selector: 'app-confirm-email-change',
  imports: [CommonModule, TranslocoDirective, RouterModule, LegalLinksContainerComponent],
  templateUrl: './confirm-email-change.component.html',
  styleUrl: './confirm-email-change.component.css',
  standalone: true,
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'auth' }]
})
export class ConfirmEmailChangeComponent implements OnInit {
  private readonly _authService = inject(AuthService);
  private readonly _userService = inject(UserService);
  private readonly _feedbackService = inject(FeedbackService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _translocoService = inject(TranslocoService);

  public isLoading = true;
  public isSuccess = false;
  public errorMessage = '';

  ngOnInit(): void {
    const token = this._route.snapshot.paramMap.get('token') ?? '';
    if (!token) {
      this.errorMessage = this._translocoService.translate(
        'confirmEmailChange.tokenMissing',
        {},
        'auth'
      );
      this.isLoading = false;
      return;
    }

    this.confirmEmailChange(token);
  }

  private async confirmEmailChange(token: string): Promise<void> {
    try {
      const result = await this._authService.confirmEmailChange(token);

      if ('notFound' in result && result.notFound) {
        this.errorMessage = this._translocoService.translate(
          'confirmEmailChange.tokenNotFound',
          {},
          'auth'
        );
        return;
      }

      const isLoggedIn = this._authService.getAccessTokenSync() != null;
      if (isLoggedIn) {
        await this._userService.loadCurrentUser();
        this._feedbackService.success(
          this._translocoService.translate('confirmEmailChange.loggedInSuccessToast', {}, 'auth')
        );
        this._router.navigate(['/main/dashboard']);
      } else {
        this.isSuccess = true;
      }
    } catch {
      this.errorMessage = this._translocoService.translate(
        'confirmEmailChange.errorMessage',
        {},
        'auth'
      );
    } finally {
      this.isLoading = false;
    }
  }
}
